import { Platform } from 'react-native';
import { Asset } from 'expo-asset';

const USE_TEST_IMAGE = process.env.EXPO_PUBLIC_USE_TEST_IMAGE === 'true';

/**
 * Checks if we're running in iOS Simulator (development mode)
 */
export const isIOSSimulator = (): boolean => {
  return Platform.OS === 'ios' && __DEV__;
};

/**
 * Checks if we should use test images for development
 * Now works on web too for easier testing
 */
export const shouldUseTestImage = (): boolean => {
  return USE_TEST_IMAGE && (__DEV__ || Platform.OS === 'web');
};

/**
 * Loads the bundled test image as a local URI.
 * 'fridge' -> sample fridge photo; 'receipt' -> the Costco receipt fixture
 * (same image as scripts/fixtures/receipts/costco-boucherville-20260717.jpeg).
 */
export const getTestImageUri = async (mode: 'fridge' | 'receipt' = 'fridge'): Promise<string> => {
  try {
    console.log(`DevelopmentUtils - Loading ${mode} test image...`);

    // Load the bundled test image
    const asset = Asset.fromModule(
      mode === 'receipt'
        ? require('../../assets/test-images/costco_receipt.jpeg')
        : require('../../assets/test-images/our_fridge_left.jpeg')
    );
    await asset.downloadAsync();

    console.log('DevelopmentUtils - Test image loaded:', asset.localUri);
    return asset.localUri || asset.uri;
  } catch (error) {
    console.error('DevelopmentUtils - Failed to load test image:', error);
    throw new Error('Failed to load test image');
  }
};

/**
 * Loads the test fridge analysis data
 */
export const getTestAnalysisData = async () => {
  try {
    console.log('DevelopmentUtils - Loading test analysis data from left_fridge_answer.json...');
    const testData = require('../../assets/test-images/left_fridge_answer.json');

    // Validate the data structure
    if (!testData.detected_items || !Array.isArray(testData.detected_items)) {
      throw new Error('Invalid test data structure: missing detected_items array');
    }

    console.log('DevelopmentUtils - Test analysis data loaded successfully:');
    console.log(`  - ${testData.detected_items.length} items detected`);
    console.log(`  - Image quality: ${testData.analysis_notes?.image_quality || 'Unknown'}`);
    console.log(`  - Items with position data: ${testData.detected_items.filter((item: any) => item.position).length}`);

    return testData;
  } catch (error) {
    console.error('DevelopmentUtils - Failed to load test analysis data:', error);
    throw new Error(`Failed to load test analysis data: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Simulates camera capture delay for more realistic testing
 */
export const simulateCameraDelay = (): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, 800));
};